import React from "react";
import {
  directChatService,
  type DirectChatBootstrapResponse,
} from "services/directChatService";
import type { CourseChatMessage } from "services/courseChatService";
import { ensureFirebaseSignedIn } from "services/firebase/firebaseAuth";
import { isFirebaseConfigured } from "services/firebase/firebaseClient";
import { listenToDirectChat } from "services/firebase/firestoreDirectChat";
import type { FirebaseConnectionMode } from "hooks/useFirebaseCourseChat";

type Handlers = {
  onMessages?: (messages: CourseChatMessage[]) => void;
  onBootstrap?: (bootstrap: DirectChatBootstrapResponse) => void;
  onIdentity?: (info: { firebaseUid: string; appUserId: number }) => void;
};

export function useFirebaseDirectChat(
  conversationId: number | string | undefined,
  handlers: Handlers,
  enabled = true
): { connectionMode: FirebaseConnectionMode } {
  const handlersRef = React.useRef(handlers);
  handlersRef.current = handlers;
  const [connectionMode, setConnectionMode] = React.useState<FirebaseConnectionMode>("connecting");

  React.useEffect(() => {
    if (!enabled || conversationId == null || conversationId === "" || !isFirebaseConfigured()) {
      setConnectionMode("error");
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const start = async () => {
      try {
        setConnectionMode("connecting");
        const bootstrap = await directChatService.bootstrap(conversationId);
        const session = await ensureFirebaseSignedIn();
        if (cancelled) return;

        handlersRef.current.onBootstrap?.(bootstrap);
        handlersRef.current.onIdentity?.({
          firebaseUid: bootstrap.firebase?.uid ?? session.firebaseUid,
          appUserId: session.appUserId,
        });

        unsubscribe = listenToDirectChat(bootstrap, session.appUserId, {
          onMessages: (messages) => handlersRef.current.onMessages?.(messages),
          onError: () => {
            if (!cancelled) setConnectionMode("error");
          },
        });

        if (!cancelled) setConnectionMode("live");
      } catch {
        if (!cancelled) setConnectionMode("error");
      }
    };

    void start();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [conversationId, enabled]);

  return { connectionMode };
}
